import React, { createContext, useContext, useCallback, useEffect, useRef, useState, ReactNode } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/authContext';
import { NotificationItem } from '../types';
import { notesService } from '../adapters/notes';

const BATMAN_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:3001').replace(/\/$/, '');

interface NotificationsContextValue {
  items: NotificationItem[];
  unread: number;
  markPanelOpened: () => void;
  clearAll: () => void;
}

const NotificationsContext = createContext<NotificationsContextValue>({
  items: [],
  unread: 0,
  markPanelOpened: () => {},
  clearAll: () => {},
});

export const useNotifications = (): NotificationsContextValue => useContext(NotificationsContext);

export const NotificationsProvider = ({ children }: { children: ReactNode }) => {
  const { userData, token } = useAuth();
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [unread, setUnread] = useState(0);
  const connectedRef = useRef(false);

  useEffect(() => {
    const rawToken = token as string | null;
    if (!rawToken || !userData) return;

    let es: EventSource | null = null;

    // Seed any already-in-progress notes as locked items on mount
    notesService.listRecentNotes().then((recent) => {
      if (recent.length === 0) return;
      setItems(prev => {
        const existingIds = new Set(prev.map(p => p.id));
        const newItems = recent
          .filter(n => !existingIds.has(n.session_id))
          .map(n => ({
            id: n.session_id,
            type: 'NOTE_PROCESSING',
            patient_name: n.patient_name,
            note_type: n.note_type,
            created_at: n.created_at,
            processing: true,
          } as NotificationItem));
        return newItems.length > 0 ? [...newItems, ...prev] : prev;
      });
    }).catch(() => {});

    const url = `${BATMAN_BASE}/user/events/stream?token=${encodeURIComponent(rawToken)}`;
    es = new EventSource(url);

    es.addEventListener('connected', () => {
      connectedRef.current = true;
    });

    es.addEventListener('audio.job.processing', (e: MessageEvent) => {
      if (!connectedRef.current) return;
      let data: any;
      try { data = JSON.parse(e.data); } catch { return; }

      const processingItem: NotificationItem = {
        id: data.session_id ?? new Date().toISOString(),
        type: 'NOTE_PROCESSING',
        patient_name: data.patient_name,
        note_type: data.note_type,
        created_at: new Date().toISOString(),
        processing: true,
      };
      setItems(prev => {
        const exists = prev.some(p => p.id === processingItem.id);
        if (exists) return prev.map(p => p.id === processingItem.id ? processingItem : p);
        return [processingItem, ...prev];
      });
    });

    es.addEventListener('audio.job.done', (e: MessageEvent) => {
      if (!connectedRef.current) return;
      let data: any;
      try { data = JSON.parse(e.data); } catch { return; }

      toast.success('Note ready');

      if (data.patient_id) {
        window.dispatchEvent(new CustomEvent('therapix:patient-notes-refresh', {
          detail: { patientId: data.patient_id },
        }));
      }

      const doneItem: NotificationItem = {
        id: data.session_id ?? new Date().toISOString(),
        type: 'NOTE_DONE',
        patient_name: data.patient_name,
        note_type: data.note_type,
        created_at: new Date().toISOString(),
        href: data.patient_id && data.session_id
          ? `/patients/${data.patient_id}/notes/${data.session_id}`
          : undefined,
        processing: false,
      };
      // Replace the locked processing entry if present, otherwise prepend
      setItems(prev => {
        const exists = prev.some(p => p.id === doneItem.id);
        if (exists) return prev.map(p => p.id === doneItem.id ? doneItem : p);
        return [doneItem, ...prev];
      });
      setUnread(prev => prev + 1);
    });

    es.addEventListener('audio.job.failed', (e: MessageEvent) => {
      if (!connectedRef.current) return;
      toast.error('Note generation failed — please try again');

      let data: any;
      try { data = JSON.parse(e.data); } catch { return; }
      if (data.session_id) {
        setItems(prev => prev.filter(p => p.id !== data.session_id));
      }
    });

    es.onerror = () => {
      connectedRef.current = false;
    };

    return () => {
      connectedRef.current = false;
      es?.close();
    };
  }, [userData, token]);

  const markPanelOpened = useCallback(() => setUnread(0), []);
  // Locked (processing) items stay until they resolve — only clear completed ones
  const clearAll = useCallback(() => setItems(prev => prev.filter(p => p.processing)), []);

  return (
    <NotificationsContext.Provider value={{ items, unread, markPanelOpened, clearAll }}>
      {children}
    </NotificationsContext.Provider>
  );
};
